import {
  ACCOUNTING_REPORT_ACTIONS,
  REPORT_CODES,
  XML_EXPORT_ACTIONS
} from '../utils/reportMetadata'
import {
  buildDownloadedFileName,
  downloadBlob,
  isoEndOfDay,
  isoStartOfDay,
  normalizePeriodToNdsCode
} from '../utils/reportFormatters'

function makeRequestId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function normalizeError(data) {
  const code = data && (data.error || (data.error_info && data.error_info.code))
  const messages = {
    access_denied: 'Недостаточно прав для просмотра отчетов',
    validation_error: 'Проверьте параметры отчета',
    report_not_found: 'Отчет не найден',
    session_revoked: 'Сессия завершена, войдите снова',
    timeout: 'Сервер не ответил на запрос отчета',
    download_failed: 'Не удалось скачать файл',
    feature_not_implemented: 'Функция не реализована на сервере',
    internal: 'Ошибка сервера при формировании отчета'
  }
  return {
    code: code || 'internal',
    message: messages[code] || (data && data.message) || 'Не удалось выполнить запрос',
    details: data && (data.details || (data.error_info && data.error_info.details)) || {}
  }
}

function waitForWsResponse({ connection, emitter, eventName, requestId, send, timeout = 60000 }) {
  return new Promise((resolve, reject) => {
    let settled = false
    let connectionReadyHandler = null

    const matchesRequest = (data) => {
      if (!requestId) {
        return true
      }
      return data.request_id === requestId || data.requesrt_id === requestId
    }

    const cleanup = () => {
      emitter.off(eventName, onResponse)
      emitter.off('accessDeniedErrorReceived', onAccessDenied)
      if (connectionReadyHandler) {
        emitter.off('connectionReady', connectionReadyHandler)
      }
      clearTimeout(timer)
    }

    const settle = (callback, payload) => {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      callback(payload)
    }

    const onResponse = (data) => {
      if (!matchesRequest(data)) {
        return
      }
      if (data.error || data.type === 'error') {
        settle(reject, normalizeError(data))
        return
      }
      settle(resolve, data)
    }

    const onAccessDenied = (data) => {
      if (!matchesRequest(data)) {
        return
      }
      settle(reject, normalizeError(data))
    }

    const timer = setTimeout(() => {
      settle(reject, normalizeError({ error: 'timeout' }))
    }, timeout)

    emitter.on(eventName, onResponse)
    emitter.on('accessDeniedErrorReceived', onAccessDenied)

    if (connection.isAuthenticated) {
      send()
      return
    }

    connectionReadyHandler = () => {
      emitter.off('connectionReady', connectionReadyHandler)
      connectionReadyHandler = null
      send()
    }
    emitter.on('connectionReady', connectionReadyHandler)
  })
}

function basePayload(connection, requestId, reportCode, filters) {
  const ndsPeriod = normalizePeriodToNdsCode(filters)
  const payload = {
    org_id: connection.org_id,
    request_id: requestId,
    report_code: reportCode,
    period_start: isoStartOfDay(filters.start),
    period_end: isoEndOfDay(filters.end),
    date: isoEndOfDay(filters.end),
    unit: 'rub',
    currency: 'RUB',
    include_diagnostics: true,
    year: ndsPeriod.year,
    period: ndsPeriod.period
  }
  if (filters.formMode) {
    payload.form_mode = filters.formMode
  }
  return payload
}

const SUBCONTO_PAYLOAD_FIELDS = [
  'subconto_types',
  'filters',
  'grouping',
  'include_subaccounts',
  'expanded_balance',
  'periodicity',
  'include_quantity',
  'include_zero'
]

function appendSubcontoPayload(payload, filters) {
  SUBCONTO_PAYLOAD_FIELDS.forEach((field) => {
    if (filters[field] !== undefined) {
      payload[field] = filters[field]
    }
  })
  return payload
}

export function buildReportArtifactPayload(connection, reportCode, filters) {
  const payload = appendSubcontoPayload(
    basePayload(connection, makeRequestId(`report-artifact-${reportCode}`), reportCode, filters),
    filters
  )
  delete payload.request_id
  return payload
}

export function getAccountingReport(connection, emitter, reportCode, filters) {
  const action = ACCOUNTING_REPORT_ACTIONS[reportCode]
  if (!action || typeof connection.send !== 'function') {
    return Promise.reject(normalizeError({ error: 'feature_not_implemented' }))
  }
  const requestId = makeRequestId(`report-${reportCode}`)
  return waitForWsResponse({
    connection,
    emitter,
    eventName: action.event,
    requestId,
    send: () => {
      connection.send(appendSubcontoPayload({
        ...basePayload(connection, requestId, reportCode, filters),
        action: action.action
      }, filters))
    }
  })
}

export function getSubcontoAnalysisReport(connection, emitter, filters) {
  return getAccountingReport(connection, emitter, REPORT_CODES.SUBCONTO, filters)
}

export function getSubcontoMetadata(connection, emitter) {
  const requestId = makeRequestId('subconto-metadata')
  return waitForWsResponse({
    connection,
    emitter,
    eventName: 'onGetSubcontoMetadata',
    requestId,
    send: () => {
      connection.send({
        action: 'get_subconto_metadata',
        request_id: requestId,
        org_id: connection.org_id
      })
    }
  })
}

export function searchSubcontoValues(connection, emitter, subcontoType, query = '', limit = 50) {
  const requestId = makeRequestId(`subconto-search-${subcontoType}`)
  return waitForWsResponse({
    connection,
    emitter,
    eventName: 'onSearchSubcontoValues',
    requestId,
    send: () => {
      connection.send({
        action: 'search_subconto_values',
        request_id: requestId,
        org_id: connection.org_id,
        subconto_type: subcontoType,
        query,
        limit
      })
    }
  })
}

export function getVatReport(connection, emitter, filters) {
  const requestId = makeRequestId('report-vat-declaration')
  return waitForWsResponse({
    connection,
    emitter,
    eventName: 'onGenerateVatReport',
    requestId,
    send: () => {
      connection.send({
        ...basePayload(connection, requestId, REPORT_CODES.NDS, filters),
        action: 'generate_vat_report'
      })
    }
  })
}

export function getBookReport(connection, emitter, reportCode, filters) {
  if (![REPORT_CODES.SELLS_BOOK, REPORT_CODES.BUYS_BOOK].includes(reportCode)) {
    return Promise.reject(normalizeError({ error: 'feature_not_implemented' }))
  }

  const requestId = makeRequestId(`report-book-${reportCode}`)
  return waitForWsResponse({
    connection,
    emitter,
    eventName: 'onGenerateVatBook',
    requestId,
    timeout: 90000,
    send: () => {
      connection.send({
        ...basePayload(connection, requestId, reportCode, filters),
        action: 'generate_vat_book'
      })
    }
  })
}

export function exportReport(connection, emitter, reportCode, filters, format = 'xml') {
  const action = XML_EXPORT_ACTIONS[reportCode]
  if (!action || typeof connection.send !== 'function') {
    return Promise.reject(normalizeError({ error: 'feature_not_implemented' }))
  }

  const requestId = makeRequestId(`report-export-${reportCode}`)
  const ndsPeriod = normalizePeriodToNdsCode(filters)

  return waitForWsResponse({
    connection,
    emitter,
    eventName: action.event,
    requestId,
    timeout: 90000,
    send: () => {
      connection.send({
        ...basePayload(connection, requestId, reportCode, filters),
        action: action.action,
        format
      })
    }
  }).then((response) => {
    return downloadBlob(
      response.url,
      buildDownloadedFileName(response, `${reportCode}-${ndsPeriod.period}.xml`)
    ).then(() => response)
  })
}

export function getAvailableReports() {
  return Promise.resolve([])
}

export function getReportMetadata() {
  return Promise.resolve(null)
}

export function isVatLikeReport(reportCode) {
  return reportCode === REPORT_CODES.NDS ||
    reportCode === REPORT_CODES.SELLS_BOOK ||
    reportCode === REPORT_CODES.BUYS_BOOK
}
